import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import {
  cacheQualifications,
  getCachedQualifications,
  cacheCheckins,
  setLastSyncTime,
} from '../db/mobileDbOperations'
import { useOnlineStatus } from './useOnlineStatus'

export function useMobileProfile(userProfileId, companyId) {
  const isOnline = useOnlineStatus()
  const [qualifications, setQualifications] = useState([])
  const [recentCheckins, setRecentCheckins] = useState([])
  const [complianceStats, setComplianceStats] = useState({
    total: 0, compliant: 0, compliance_percentage: 0,
    valid: 0, expiring: 0, expired: 0, missing: 0, booked: 0, quoting: 0,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchQualifications = useCallback(async () => {
    if (!userProfileId || !companyId) return

    try {
      const { data, error: rpcError } = await supabase.rpc('app_get_profile_qualifications_v5', {
        p_user_id: userProfileId,
        p_company_id: companyId,
      })

      if (rpcError) throw rpcError

      const quals = data?.qualifications || []
      const stats = data?.compliance_stats || {}

      setQualifications(quals)
      setComplianceStats(stats)
      await cacheQualifications(quals)
    } catch (err) {
      console.error('[Profile] Error fetching qualifications:', err)
      setError(err.message)
    }
  }, [userProfileId, companyId])

  const fetchRecentCheckins = useCallback(async () => {
    if (!userProfileId) return

    try {
      const { data, error: rpcError } = await supabase.rpc('app_get_recent_checkins_v2', {
        p_user_id: userProfileId,
        p_days: 7,
      })

      if (rpcError) throw rpcError

      const checkins = data || []
      setRecentCheckins(checkins)
      await cacheCheckins(checkins.map(c => ({ ...c, syncStatus: 'synced' })))
    } catch (err) {
      console.error('[Profile] Error fetching checkins:', err)
    }
  }, [userProfileId])

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)

    if (isOnline) {
      await Promise.all([fetchQualifications(), fetchRecentCheckins()])
      await setLastSyncTime()
    } else {
      try {
        const cached = await getCachedQualifications()
        if (cached?.length) {
          setQualifications(cached)
        }
      } catch {
        // No cached data
      }
    }

    setLoading(false)
  }, [isOnline, fetchQualifications, fetchRecentCheckins])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  return {
    qualifications,
    recentCheckins,
    complianceStats,
    loading,
    error,
    refetch: fetchAll,
  }
}
